/**
 * @file useSectionExtras.ts
 * @description Связь редактора секции со страницей (phase19.16 Р2а;
 * uiux-design-r2a §3, §6, §7): контекст секции (обычная страница, место,
 * шаблон товара), где в шаблоне уже стоят элементы данных товара («Уже есть
 * в блоке «…» [Показать]»), и «Перенести в другой блок ›».
 *
 * Перенос: несохранённые правки блока сначала сохраняются (иначе сервер
 * перенёс бы старое), затем `POST blocks/:id/elements/move`, страница
 * перечитывается, канва перерисовывается, выбор переходит на элемент в
 * новом блоке. Ошибки (409 `product_template_required`, 422
 * `product_template_invalid`, `block_changed`) — словами владельца.
 */

import type { SectionPayload } from "@chaiberi/shared";
import { useCallback, useMemo, useState } from "react";

import { useBuilder } from "../../components/site-builder/builder-context";
import type { MoveTarget } from "../../components/site-builder/section/MoveToBlockMenu";
import type { SectionFormContextValue } from "../../components/site-builder/section/section-form-context";
import { isSectionBlock, blockTitle } from "../../components/site-pages/block-meta";
import { useNotify } from "../../components/ui/NotifyProvider";
import type { SitePageBlock } from "../../lib/api-site-pages";
import { moveElementToBlock, TO_END } from "../../lib/api-site-templates";
import { blockErrorText } from "../../lib/site-builder/block-errors";
import { ELEMENT_KIND_META } from "../../lib/site-builder/section-meta";
import { findElement } from "../../lib/site-builder/section-edit";
import { productElementIndex, sectionContextOf, type ElementRef } from "../../lib/site-builder/template-rules";

interface Deps {
  block: SitePageBlock;
  payload: SectionPayload;
  title: string;
  dirty: boolean;
  save: () => Promise<boolean>;
  /** Выбрать элемент этого блока. */
  selectElement: (elementId: string | null) => void;
}

export interface MoveDialogState {
  open: boolean;
  elementLabel: string;
  targets: MoveTarget[];
  busy: boolean;
  onPick: (blockId: string, column: number) => void;
  onClose: () => void;
}

/**
 * id перенесённого элемента в блоке-получателе: занятый там id сервер
 * заменяет свободным (`<id>-m2`, §19.9) — берём его из ответа.
 */
export function movedElementId(before: SitePageBlock | undefined, after: SitePageBlock | undefined, elementId: string): string {
  const ids = (block: SitePageBlock | undefined) =>
    block && isSectionBlock(block) ? (block.payload as SectionPayload).columns.items.flatMap((c) => c.elements.map((el) => el.id)) : [];
  const old = new Set(ids(before));
  return ids(after).find((id) => !old.has(id) && (id === elementId || id.startsWith(`${elementId}-m`))) ?? elementId;
}

/** Даём React дорисовать перечитанную страницу (форма блока перестаёт быть «изменённой»). */
const nextTick = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

export function useSectionExtras({ block, payload, title, dirty, save, selectElement }: Deps) {
  const b = useBuilder();
  const notify = useNotify();
  const { pages } = b;
  const blocks = useMemo(() => pages.page?.blocks ?? [], [pages.page]);
  const context = sectionContextOf(pages.page?.kind);
  const [moving, setMoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const taken = useMemo(
    () => (context === "productTemplate" ? productElementIndex(blocks, { id: block.id, title, payload }) : {}),
    [blocks, block.id, context, payload, title],
  );

  const showElement = useCallback(
    async (ref: ElementRef) => {
      if (ref.blockId === block.id) {
        selectElement(ref.elementId);
        return;
      }
      if (await b.select({ sel: { kind: "block", id: ref.blockId }, panel: null })) b.setElement({ blockId: ref.blockId, elementId: ref.elementId });
    },
    [b, block.id, selectElement],
  );

  const targets = useMemo<MoveTarget[]>(
    () => blocks.filter((x) => x.id !== block.id && isSectionBlock(x)).map((x) => ({ id: x.id, title: blockTitle(x), columns: (x.payload as SectionPayload).columns.items.map((c) => c.elements.length) })),
    [blocks, block.id],
  );

  const pick = useCallback(
    async (toBlockId: string, toColumn: number) => {
      if (!moving) return;
      setBusy(true);
      try {
        const wasDirty = dirty;
        if (wasDirty && !(await save())) return;
        const target = blocks.find((x) => x.id === toBlockId);
        const res = await moveElementToBlock(block, {
          elementId: moving,
          toBlockId,
          toColumn,
          toIndex: TO_END,
          expectedUpdatedAt: wasDirty ? undefined : block.updatedAt,
          toExpectedUpdatedAt: target?.updatedAt,
        });
        // Записи «Отменить» о блоках до переноса вернули бы элемент вторым экземпляром.
        b.undo.clear();
        await pages.reloadPage();
        pages.bumpCanvas();
        await nextTick();
        setMoving(null);
        const elementId = movedElementId(target, res.blocks.find((x) => x.id === toBlockId), moving);
        if (await b.select({ sel: { kind: "block", id: toBlockId }, panel: null })) b.setElement({ blockId: toBlockId, elementId });
        notify.success("Элемент перенесён");
      } catch (err) {
        notify.error(blockErrorText(err, "Не удалось перенести элемент."));
        void pages.reloadPage();
      } finally {
        setBusy(false);
      }
    },
    [b, block, blocks, dirty, moving, notify, pages, save],
  );

  const movingEl = moving ? findElement(payload, moving)?.element : null;
  const dialog: MoveDialogState = {
    open: moving !== null,
    elementLabel: movingEl ? ELEMENT_KIND_META[movingEl.kind].label : "",
    targets,
    busy,
    onPick: (id, col) => void pick(id, col),
    onClose: () => setMoving(null),
  };

  const canMove = b.canEditPages;
  const formExtra = useMemo<Partial<SectionFormContextValue>>(
    () => ({ context, taken, showElement: (ref) => void showElement(ref), moveElement: canMove ? setMoving : null }),
    [canMove, context, showElement, taken],
  );
  return { formExtra, dialog };
}
